import React, { useEffect, useMemo, useRef } from "react"
import { GlobalSidebar, SidebarList, FormInputAntd, AntdButton, FormCheckbox } from "components"
import { FormProvider, SubmitHandler, useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import { SDQualificationInitialValues, SDQualificationSchema } from "Schema"
import { SDQualificationInterface } from "@type/staticData.types"
import useAppSelector from "hooks/useAppSelector"
import useAppDispatch from "hooks/useAppDispatch"
import {
	getSDQualifications,
	isSDQualificationsLoading,
	isSDQualificationsSaving,
	saveSDQualification,
	selectSDQualificationsById,
	selectSDQualificationsList,
} from "store/StaticData/SDQualifications.slice"
import { useAdminQueryParams } from "hooks"
import { sortByProperty } from "config/utils"

interface SDQualificationFormHandler extends SDQualificationInterface {}

const SDQualifications = () => {
	const dispatch = useAppDispatch()
	const loading = useAppSelector(isSDQualificationsLoading())
	const saving = useAppSelector(isSDQualificationsSaving())
	const SDQualificationsList = useAppSelector(selectSDQualificationsList)
	const { params, setSearchParams } = useAdminQueryParams()
	const { selected: selectedSDQualification = "" } = params
	const SDQualification = useAppSelector(selectSDQualificationsById(selectedSDQualification))
	const isNewBeingCreated = useRef(false)

	const methods = useForm<SDQualificationFormHandler>({ resolver: yupResolver(SDQualificationSchema) })
	const {
		handleSubmit,
		reset,
		formState: { isDirty, isSubmitting, isSubmitted },
	} = methods

	const sidebarSDQualifications = useMemo(() => {
		return (SDQualificationsList ?? [])?.map(SDQualification => ({
			_id: SDQualification._id,
			name: SDQualification.name,
		}))
	}, [saving, SDQualificationsList?.length, isSubmitted])

	const onSubmit: SubmitHandler<SDQualificationFormHandler> = data => {
		isNewBeingCreated.current = true
		dispatch(
			saveSDQualification(data, id => {
				setSearchParams({ selected: id })
				reset({ ...data })
			}),
		)
	}

	useEffect(() => {
		dispatch(getSDQualifications())
	}, [])

	useEffect(() => {
		if (!selectedSDQualification && !!SDQualificationsList?.length && !isNewBeingCreated.current) {
			setSearchParams({ selected: SDQualificationsList?.[0]?._id })
		}
	}, [params, SDQualificationsList])

	useEffect(() => {
		if (selectedSDQualification && SDQualification) {
			reset({ ...SDQualificationInitialValues, ...SDQualification })
		} else if (!selectedSDQualification && !isNewBeingCreated.current) {
			reset({ ...SDQualificationInitialValues })
		}
	}, [selectedSDQualification, SDQualification])

	const handleSelected = (SDQualificationId: string): void => {
		setSearchParams({ selected: SDQualificationId })
	}

	return (
		<>
			<GlobalSidebar>
				<SidebarList
					title={"SDQualifications"}
					loading={loading}
					selected={selectedSDQualification}
					setSelected={handleSelected}
					list={sidebarSDQualifications.sort(sortByProperty("name"))}
				/>
			</GlobalSidebar>
			<FormProvider {...methods}>
				<form onSubmit={handleSubmit(onSubmit)}>
					<div className="flex w-1/4 flex-col space-y-3">
						<div className="flex flex-col space-y-3">
							<FormInputAntd type="text" label="Qualification Name" name="name" />
							<FormCheckbox label="Can be Overridden" name="canBeOverRidden" />
						</div>
						<div className="flex gap-2">
							<AntdButton
								onClick={() => {
									isNewBeingCreated.current = true
									reset({ ...SDQualificationInitialValues })
									setSearchParams({ selected: "" })
								}}
							>
								New
							</AntdButton>
							<AntdButton disabled={!isDirty || isSubmitting} loading={saving} htmlType="submit">
								{selectedSDQualification ? "Update" : "Save"}
							</AntdButton>
						</div>
					</div>
				</form>
			</FormProvider>
		</>
	)
}

export default SDQualifications
